import React, { useEffect, useRef, useState } from 'react';
import './Approvisionnement.css';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ RÉGLAGES LARGEURS COLONNES — ajuste les valeurs px selon tes besoins
const COLUMN_WIDTHS = {
  'Item':                                        '140px',
  'Description':                                 '240px',
  "Prix d'achat":                                '85px',
  'Prix de vente':                               '85px',
  "Délai d'approvisionnement (jours ouvrés)":    '85px',
  'Délais réel':                                  '85px',
  'Statut appro':                                '75px',
  'Stock':                                        '75px',
  'Cmde client':                                  '80px',
  'Cmde fournisseur':                             '80px',
  'Stock projeté':                                '80px',
  'Stock disponible (non alloué)':               '85px',
  'Dernier inventaire':                          '90px',
  'Valeur de stock':                             '90px',
  'Valeur de stock projeté':                     '90px',
  'Evolution stock en valeur':                   '90px',
  'Stock mini':                                   '80px',
  'Date de livraison':                           '100px',
  'Date de rupture':                             '100px',
};
// Toute colonne absente du mapping reçoit cette largeur par défaut
const DEFAULT_WIDTH = '120px';

// ★ RENOMMAGE COLONNES — nom affiché dans l'en-tête (la clé reste le nom réel en base)
const COLUMN_LABELS = {
  "Délai d'approvisionnement (jours ouvrés)": 'Délais appro',
  'Cmde fournisseur':                         'Cmde frn',
  'Stock disponible (non alloué)':            'Stock dispo (non alloué)',
  'Evolution stock en valeur':                'Evol stock €',
  'Stock projeté':                            'Stock proj',
  'Dernier inventaire':                            'Dernier invent',
};

// ── colonnes entières (sans décimale) ───────────────────────────────────────
const NO_DECIMAL_COLS = new Set([
  'Délais réel',
  'Stock',
  'Cmde client',
  'Cmde fournisseur',
  'Stock projeté',
  'Stock disponible (non alloué)',
  'Stock mini',
]);

// Largeur totale = somme des colonnes réellement affichées (fige le layout ;
// si elle dépasse l'écran → barre de défilement horizontale dans le cadre du tableau)
const calcTotalWidth = (cols) =>
  cols.reduce((sum, c) => sum + parseInt(COLUMN_WIDTHS[c] || DEFAULT_WIDTH, 10), 0);

// ★ Le cadre du tableau est calculé sur la fenêtre du navigateur (bords + barre toujours visibles)
const MARGE_DROITE  = 24;   // px laissés à droite du tableau
const MARGE_BAS     = 24;   // px laissés sous la barre de défilement
const HAUTEUR_BARRE = 34;   // px réservés pour la barre horizontale + boutons ◀ ▶

// ── Dates : jj/mm/aaaa, sans heure ──────────────────────────────────────────
//   "2026-05-09" / "2026-05-09T00:00:00"  → découpage direct (aucun décalage)
//   "2026-05-08T22:00:00.000Z" (PostgreSQL en UTC) → heure locale = 09/05/2026
//   "09/05/2026" → inchangée · toute autre valeur → inchangée
const pad2 = (n) => String(n).padStart(2, '0');
const formatDate = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const s = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return value;
  const avecFuseau = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
  if (avecFuseau && !(m[4] === '00' && m[5] === '00')) {
    const d = new Date(s);
    if (!isNaN(d)) return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  return `${m[3]}/${m[2]}/${m[1]}`;
};
// Une valeur est une date si elle ressemble à AAAA-MM-JJ (quelle que soit la colonne : Date de livraison, Date de rupture, Dernier inventaire…)
const estDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v.trim());

// ── couleurs ─────────────────────────────────────────────────────────────────
const RED_CELL   = { backgroundColor: '#f9d3d3', color: '#c0392b' };
const AMBER_CELL = { backgroundColor: '#fff9c4', color: '#b7770d' };

const Donneitem = () => {
  const [data, setData]             = useState([]);
  const [filteredData, setFiltered] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('');

  useEffect(() => {
    document.title = 'MYSSA – base article';
  }, []);

  useEffect(() => {
    fetch(`${API}/base_de_donne_item`)
      .then(r => r.json())
      .then(d => { setData(d); setFiltered(d); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const columns = data.length > 0
    ? Object.keys(data[0]).filter(c => c !== 'Id' && c !== 'Client_id')
    : [];

  const applyFilter = (txt) => {
    setFiltered(
      data.filter(item => item.Item?.toLowerCase().includes(txt.toLowerCase()))
    );
  };

  const inputStyle = {
    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: '12px',
    padding: '6px 10px', border: '1px solid #e2d9cc', borderRadius: '6px',
    outline: 'none', background: '#fdfcfa', color: '#1e293b', width: '220px', maxWidth: '100%',
  };

  const totalWidth = calcTotalWidth(columns);

  // ── Barre de défilement horizontale sous le tableau (synchronisée avec le tableau) ──
  const wrapRef = useRef(null);                     // cadre du tableau
  const barRef  = useRef(null);                     // barre sous le tableau
  const synchro = useRef(false);                    // évite la boucle tableau ↔ barre
  const [largeurs, setLargeurs] = useState({ contenu: 0, visible: 0 });
  // ★ Taille du cadre calculée sur la fenêtre du navigateur : le tableau ET sa barre restent entièrement visibles
  const [cadre, setCadre] = useState({ largeur: null, hauteur: null });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const mesurer = () => {
      const r = el.getBoundingClientRect();
      const haut = r.top + window.scrollY;          // position du tableau dans la page
      setCadre({
        largeur: Math.max(280, Math.floor(window.innerWidth - r.left - MARGE_DROITE)),
        hauteur: Math.max(220, Math.floor(window.innerHeight - haut - HAUTEUR_BARRE - MARGE_BAS)),
      });
      setLargeurs({ contenu: el.scrollWidth, visible: el.clientWidth, barre: barRef.current ? barRef.current.clientWidth : 0 });
    };
    mesurer();
    const t = setTimeout(mesurer, 50);              // 2e mesure une fois la page posée
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(mesurer) : null;
    if (ro) { ro.observe(el); if (el.firstElementChild) ro.observe(el.firstElementChild); }
    window.addEventListener('resize', mesurer);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); window.removeEventListener('resize', mesurer); };
  }, [loading, columns.length, filteredData.length, largeurs.contenu > largeurs.visible + 1]);

  // ── Navigation gauche / droite à la souris ──
  // 1) Molette au-dessus de la barre → défile à gauche / droite
  // 2) Maj + molette sur le tableau → défile à gauche / droite
  // 3) Cliquer-glisser dans le tableau → déplace le tableau comme une carte
  // 4) Boutons ◀ ▶ à côté de la barre
  const decaler = (dx) => { if (wrapRef.current) wrapRef.current.scrollBy({ left: dx, behavior: 'smooth' }); };
  const molette = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const versBarre = e.currentTarget === barRef.current;
    if (versBarre || e.shiftKey) {
      el.scrollLeft += (e.deltaY || e.deltaX);
    }
  };
  const glisse = useRef(null);
  const debutGlisse = (e) => {
    if (e.button !== 0 || ['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'A'].includes(e.target.tagName)) return;
    const el = wrapRef.current;
    glisse.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, bouge: false };
    const deplacer = (ev) => {
      const g = glisse.current;
      if (!g) return;
      const dx = ev.clientX - g.x, dy = ev.clientY - g.y;
      if (!g.bouge && Math.abs(dx) + Math.abs(dy) < 4) return;   // simple clic : on ne bouge pas
      g.bouge = true;
      el.classList.add('di-grabbing');
      el.scrollLeft = g.left - dx;
      el.scrollTop = g.top - dy;
      ev.preventDefault();
    };
    const fin = () => {
      glisse.current = null;
      el.classList.remove('di-grabbing');
      window.removeEventListener('mousemove', deplacer);
      window.removeEventListener('mouseup', fin);
    };
    window.addEventListener('mousemove', deplacer);
    window.addEventListener('mouseup', fin);
  };

  const defiler = (source, cible) => {
    if (synchro.current) { synchro.current = false; return; }
    if (!source || !cible) return;
    synchro.current = true;
    cible.scrollLeft = source.scrollLeft;
  };
  const barreUtile = largeurs.contenu > largeurs.visible + 1;

  if (loading) return (
    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", padding: '20px', color: '#8b7b64' }}>
      Chargement…
    </div>
  );

  return (
    <div className="di-page">
      {/* ★ Mise en page responsive : le tableau prend la largeur et la hauteur de la fenêtre ;
            s'il est plus large que l'écran → barre de défilement horizontale dans son cadre.
            En-têtes figés en haut pendant le défilement vertical. Données centrées. */}
      <style>{`
        .di-page {
          font-family: 'IBM Plex Sans', sans-serif;
          width: 100%; max-width: 100%; min-width: 0;
          box-sizing: border-box; padding: 16px 16px 16px 0;
          display: flex; flex-direction: column; gap: 120px;
        }
        .di-page { max-width: 100vw; }
        .di-head { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 20px; }
        .di-title-row { display: flex; align-items: center; gap: 10px; }
        .di-count { font-size: 12px; color: #8b7b64; }
        .di-page .di-table-wrap {
          position: static !important;
          margin: 0 !important;
          overflow-x: auto !important; overflow-y: auto !important;
          box-sizing: border-box;
          border: 1px solid #cbd5e1; border-radius: 8px;   /* contour du tableau */
          background: #ffffff;
          cursor: grab;
          -webkit-overflow-scrolling: touch;
        }
        .di-page .di-table-wrap.di-grabbing { cursor: grabbing; user-select: none; }
        .di-page .di-table-wrap table { border-collapse: collapse; }
        /* fond des en-têtes figés (priorité nulle : la couleur de Approvisionnement.css reste prioritaire) */
        :where(.di-page .di-table-wrap thead th) { background-color: #f4f6f9; }
        .di-page .di-table-wrap thead th {
          position: sticky; top: 0; z-index: 2;
          text-align: center !important; vertical-align: middle;
          white-space: normal; word-break: break-word;
        }
        .di-page .di-table-wrap td {
          text-align: center !important; vertical-align: middle;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        /* Barre horizontale sous le tableau : toujours visible, bien épaisse */
        .di-nav { display: flex; align-items: center; gap: 8px; margin-top: -8px; }
        .di-nav-btn {
          flex: 0 0 auto; width: 30px; height: 22px; padding: 0; margin: 0;
          border: 1px solid #cbd5e1; border-radius: 6px; background: #ffffff; color: #334155;
          font-size: 12px; line-height: 1; cursor: pointer;
        }
        .di-nav-btn:hover { background: #e2e8f0; }
        .di-hscroll {
          flex: 1 1 auto; min-width: 0;
          overflow-x: scroll; overflow-y: hidden;
          height: 18px;
          background: #f4f6f9; border-radius: 8px;
          scrollbar-width: auto; scrollbar-color: #94a3b8 #f4f6f9;
        }
        .di-hscroll::-webkit-scrollbar { height: 14px; }
        .di-hscroll::-webkit-scrollbar-track { background: #f4f6f9; border-radius: 8px; }
        .di-hscroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 8px; border: 3px solid #f4f6f9; }
        .di-hscroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
        /* La barre native du cadre est masquée (Chrome / Edge) pour ne pas en avoir deux */
        .di-page .di-table-wrap::-webkit-scrollbar { width: 10px; height: 0; }
        .di-page .di-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
        .di-page .di-table-wrap::-webkit-scrollbar-track { background: transparent; }

        @media (max-width: 900px) {
          .di-page { padding-right: 0; }
        }
      `}</style>

      {/* Titre + filtre */}
      <div className="di-head">
        <div className="di-title-row">
          <div style={{ width: '3px', height: '18px', background: 'linear-gradient(180deg, #04202d, #38bdf8)', borderRadius: '2px' }} />
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '20px', fontWeight: '200', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#04202d' }}>
            Données article
          </span>
        </div>
        <input
          style={inputStyle}
          placeholder="Filtrer par Item…"
          aria-label="Filtrer par Item"
          value={filter}
          onChange={e => { setFilter(e.target.value); applyFilter(e.target.value); }}
        />
        <span className="di-count">
          {filteredData.length}{filteredData.length !== data.length ? ` / ${data.length}` : ''} article{filteredData.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Tableau */}
      <div
        className="table-container1 di-table-wrap"
        ref={wrapRef}
        onScroll={() => defiler(wrapRef.current, barRef.current)}
        onWheel={molette}
        onMouseDown={debutGlisse}
        title="Cliquer-glisser ou Maj + molette pour aller à gauche / droite"
        style={{
          width: cadre.largeur ? `${cadre.largeur}px` : '100%',
          maxWidth: cadre.largeur ? `${cadre.largeur}px` : '100%',
          height: cadre.hauteur ? `${cadre.hauteur}px` : '60vh',
        }}
      >
        <table style={{ tableLayout: 'fixed', width: `${totalWidth}px`, minWidth: '100%' }}>

          {/* ★ Largeurs colonnes — modifie COLUMN_WIDTHS en haut du fichier */}
          <colgroup>
            {columns.map(c => (
              <col key={c} width={COLUMN_WIDTHS[c] || DEFAULT_WIDTH} />
            ))}
          </colgroup>

          <thead>
            <tr>{columns.map(c => <th key={c} title={c}>{COLUMN_LABELS[c] || c}</th>)}</tr>
          </thead>
          <tbody>
            {filteredData.length === 0
              ? <tr><td colSpan={columns.length}>Aucune donnée</td></tr>
              : filteredData.map((item, i) => {
                // ── flag ligne : stock projeté négatif ──────────────────────
                const stockProjete = parseFloat(item['Stock projeté']);
                const stockNegatif = !isNaN(stockProjete) && stockProjete < 0;

                return (
                  <tr key={i}>
                    {columns.map((col, j) => {
                      let value = item[col];
                      let style = {};

                      // Valeurs monétaires
                      if (col === 'Valeur de stock' || col === 'Valeur de stock projeté') {
                        const n = parseFloat(value);
                        // Valeur de stock projeté → 0€ si stock projeté négatif
                        if (col === 'Valeur de stock projeté' && stockNegatif) {
                          value = '0€';
                        } else {
                          value = !isNaN(n) ? `${Math.round(n)}€` : value;
                        }
                      }

                      // Évolution stock en valeur (%)
                      if (col === 'Evolution stock en valeur') {
                        const n = parseFloat(value);
                        value = !isNaN(n) ? `${Math.round(n)}%` : value;
                        if (n > 150)       style = AMBER_CELL;
                        else if (n >= 130) style = AMBER_CELL;
                      }

                      // Colonnes entières — pas de décimale
                      if (NO_DECIMAL_COLS.has(col)) {
                        const n = parseFloat(value);
                        value = !isNaN(n) ? Math.round(n) : value;
                      }

                      // Dates — format jj/mm/aaaa sans heure (toutes les colonnes de type date)
                      if (estDate(value)) {
                        value = formatDate(value);
                      }

                      // ── colorisation conditionnelle ──────────────────────
                      // Stock projeté < 0 → rouge
                      if (col === 'Stock projeté' && stockNegatif) {
                        style = RED_CELL;
                      }
                      // Stock disponible (non alloué) → rouge si stock projeté < 0
                      if (col === 'Stock disponible (non alloué)' && stockNegatif) {
                        style = RED_CELL;
                      }
                      // Statut appro : "-" → "ok"
                      if (col === 'Statut appro' && String(value).trim() === '-') {
                        value = 'ok';
                      }
                      // Statut appro = "rupture" → rouge
                      if (col === 'Statut appro' &&
                          String(value).trim().toLowerCase() === 'rupture') {
                        style = RED_CELL;
                      }

                      return <td key={j} style={style} title={value ?? ''}>{value}</td>;
                    })}
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </div>

      {/* ★ Barre de défilement horizontale sous le tableau (affichée seulement si le tableau dépasse l'écran) */}
      {barreUtile && (
        <div className="di-nav" style={{ width: cadre.largeur ? `${cadre.largeur}px` : '100%' }}>
          <button type="button" className="di-nav-btn" onClick={() => decaler(-400)} aria-label="Aller à gauche">◀</button>
          <div
            className="di-hscroll"
            ref={barRef}
            onScroll={() => defiler(barRef.current, wrapRef.current)}
            onWheel={molette}
            role="scrollbar"
            aria-orientation="horizontal"
            aria-label="Faire défiler le tableau de gauche à droite"
          >
            <div style={{ width: `${largeurs.contenu - largeurs.visible + (largeurs.barre || largeurs.visible)}px`, height: '1px' }} />
          </div>
          <button type="button" className="di-nav-btn" onClick={() => decaler(400)} aria-label="Aller à droite">▶</button>
        </div>
      )}
    </div>
  );
};

export default Donneitem;